/**
 * O CONTRATO DA ABA "PLANTÃO" entre o servidor e a tela (FECHAMENTO-DO-PLANTAO, 06/10/2026).
 *
 * 🔴 Os códigos que as duas camadas comparam com `===` (situação, empresa, motivo da conferência, modo de cobrança)
 * nascem AQUI e o servidor os importa — a direção da casa é servidor → tipos do front. Escrever o mesmo literal nos
 * dois lados quebra sem erro e sem aviso (régua de 30/09).
 */

export const SITUACOES = ['a_faturar', 'faturado', 'nao_cobrar', 'conferencia'] as const;
export type Situacao = (typeof SITUACOES)[number];

/** O que a pessoa escolhe no seletor da linha (conferência não é escolha: é estado). */
export type SituacaoEscolhida = Exclude<Situacao, 'conferencia'>;

export const ROTULO_DA_SITUACAO: Record<Situacao, string> = {
  a_faturar: 'A faturar',
  faturado: 'Faturado',
  nao_cobrar: 'Não cobrar',
  conferencia: 'Em conferência',
};

export type Empresa = 'IGM' | 'ROUTE';

export type ModoDeCobranca = 'por_chamado' | 'por_hora' | 'primeira_hora_demais';

export const ROTULO_DO_MODO: Record<ModoDeCobranca, string> = {
  por_chamado: 'Por atendimento',
  por_hora: 'Por hora',
  primeira_hora_demais: '1ª hora + demais',
};

export type MotivoDeConferencia =
  | 'sem_tempo'
  | 'cliente_nao_reconhecido'
  | 'sem_contrato_ativo'
  | 'sem_regra'
  | 'regras_diferentes'
  | 'franquias_diferentes'
  | 'tabela_route_ausente'
  /** #515: corretiva avulsa de cliente com contrato em implantação (não cobra sozinha). */
  | 'contrato_em_implantacao'
  /** #515: o valor por hora da corretiva avulsa ainda não foi cadastrado em Configurações. */
  | 'avulsa_sem_valor';

/** O motivo da conferência em poucas palavras, para coluna de tabela (PDF e tela). A frase inteira continua no `motivoTexto`. */
export const ROTULO_CURTO_DA_CONFERENCIA: Record<MotivoDeConferencia, string> = {
  sem_tempo: 'Tempo não medido',
  cliente_nao_reconhecido: 'Cliente não ligado',
  sem_contrato_ativo: 'Sem contrato ativo',
  sem_regra: 'Contrato sem regra',
  regras_diferentes: 'Regras diferentes',
  franquias_diferentes: 'Franquias diferentes',
  tabela_route_ausente: 'Sem tabela da Route',
  contrato_em_implantacao: 'Contrato em implantação',
  avulsa_sem_valor: 'Avulsa sem valor',
};

export interface RegraDePreco {
  modo: ModoDeCobranca;
  valorCentavos: number | null;
  primeiraHoraCentavos: number | null;
  demaisCentavos: number | null;
}

export type ContaDoPlantao =
  | { situacao: 'cobravel'; minutos: number; horasCobradas: number; valorCentavos: number; regraTexto: string }
  | {
      situacao: 'nao_cobra';
      minutos: number | null;
      horasCobradas: null;
      valorCentavos: 0;
      regraTexto: string;
      porFranquia?: boolean;
      /** Cliente sem contrato ativo no dia, com contrato em implantação: não se cobra (Matheus, 29/10/2026). */
      porImplantacao?: boolean;
    }
  | {
      situacao: 'conferencia';
      minutos: number | null;
      horasCobradas: number | null;
      valorCentavos: null;
      regraTexto: string;
      motivo: MotivoDeConferencia;
      motivoTexto: string;
      regrasEmConflito?: RegraDePreco[];
    };

/**
 * De onde a linha vem: um PLANTÃO, ou uma CORRETIVA que passou da franquia do mês (P10 — o mesmo controle de situação,
 * a mesma tabela, total próprio), ou (#515) uma CORRETIVA AVULSA: sistema que nenhum contrato ativo do cliente cobre,
 * cobrada pelo valor por hora de Configurações.
 */
export type OrigemDaLinha = 'plantao' | 'corretiva_excedente' | 'corretiva_avulsa';

/**
 * "Não cobrar" tem três origens, separadas nos dados e nos totais: pelo CONTRATO (não cobra plantão), pela FRANQUIA do
 * contrato (D1 — dentro da franquia do mês) e por DECISÃO de gente (com motivo da lista).
 */
export type TipoDeNaoCobrar = 'contrato' | 'franquia' | 'decisao';

/**
 * 29/10/2026 (Matheus: "contrato em implantação nao deverá ser cobrado pois está em implantação"): o plantão e a
 * corretiva avulsa de cliente sem contrato ativo no dia, mas com contrato em implantação, nascem "Não cobrar" com este
 * motivo, sozinhos. Conta junto do "Pelo contrato" (é o contrato que decide), com o nome próprio na linha.
 */
export const MOTIVO_IMPLANTACAO = 'Contrato em implantação';

/** 29/10/2026: o "Lançar" da conferência aplica a tabela de plantão sem contrato; este é o motivo que fica escrito. */
export const MOTIVO_DO_LANCAR = 'Tabela de plantão sem contrato';

export interface LinhaDoPlantao {
  taskId: string;
  /** Ausente em resposta antiga = plantão. */
  origem?: OrigemDaLinha;
  /** O sistema do atendimento (da corretiva excedente; no plantão, quando o tipo diz). */
  sistemaNome?: string | null;
  /** `YYYY-MM-DD` */
  data: string;
  entradaIso: string | null;
  tecnico: string;
  clienteId: string | null;
  clienteNome: string;
  empresa: Empresa;
  urlNoAuvo: string | null;
  minutosNoLocal: number | null;
  conta: ContaDoPlantao;
  situacao: Situacao;
  naoCobrarTipo: TipoDeNaoCobrar | null;
  motivo: { id: string | null; nome: string; texto: string | null } | null;
  /** O valor que conta: gravado (Faturado) ou ao vivo (A faturar). `null` em Não cobrar e conferência. */
  valorCentavos: number | null;
  horasCobradas: number | null;
  regraTexto: string;
  valorMudou: { eraCentavos: number; hojeCentavos: number } | null;
  mudadoPor: string | null;
  mudadoEmIso: string | null;
  ajuste: { tipo: 'tempo' | 'valor' | 'regra'; motivo: string; por: string; emIso: string } | null;
  /**
   * 01/11/2026: o admin trocou a empresa deste plantão (o tipo da tarefa no Auvo dizia a outra). `empresa` acima já é a
   * corrigida; aqui fica a do Auvo, o motivo, quem e quando. Ausente ou `null` = vale a do Auvo.
   */
  correcaoDaEmpresa?: CorrecaoDaEmpresa | null;
}

/** 01/11/2026 — a empresa trocada pelo admin (plantao_correcao). */
export interface CorrecaoDaEmpresa {
  doAuvo: Empresa;
  motivo: string;
  por: string;
  emIso: string;
}

/** 01/11/2026 — um plantão excluído da aba pelo admin: fica fora de toda conta, listado em "Excluídos do plantão". */
export interface PlantaoExcluido {
  taskId: string;
  data: string;
  entradaIso: string | null;
  tecnico: string;
  clienteId: string | null;
  clienteNome: string;
  empresa: Empresa;
  urlNoAuvo: string | null;
  motivo: string;
  por: string;
  emIso: string;
}

export interface CartoesDoPlantao {
  totalCentavos: number;
  igmCentavos: number;
  routeCentavos: number;
  atendimentos: number;
  cobraveis: number;
  naoCobrados: number;
  naoCobradosContrato: number;
  /** D1: dentro da franquia de plantão do contrato. */
  naoCobradosFranquia: number;
  naoCobradosDecisao: number;
  emConferencia: number;
  horasCobradas: number;
  minutosNoLocal: number;
  faturadoCentavos: number;
  faltaFaturarCentavos: number;
}

export interface HorasDoTecnico {
  tecnico: string;
  atendimentos: number;
  minutosNoLocal: number;
  horasCobradas: number;
}

export interface ValorDoMes {
  /** `YYYY-MM` */
  mes: string;
  igmCentavos: number;
  routeCentavos: number;
  emAndamento: boolean;
  naoCobradosContrato: number;
  naoCobradosFranquia: number;
  naoCobradosDecisao: number;
}

export interface NaoCobradoDoCliente {
  clienteId: string | null;
  clienteNome: string;
  noMes: number;
  ultimos3Meses: number;
  minutosNoMes: number;
  acimaDoLimite: boolean;
}

export interface NaoCobrados {
  quantidade: number;
  minutos: number;
  contrato: { quantidade: number; minutos: number };
  /** D1: dentro da franquia de plantão do contrato. */
  franquia: { quantidade: number; minutos: number };
  decisao: { quantidade: number; minutos: number };
  porMotivo: Array<{ motivo: string; quantidade: number; minutos: number }>;
  clientes: NaoCobradoDoCliente[];
  limite: number;
}

export interface MotivoDeNaoCobrar {
  id: string;
  nome: string;
  /** 1 = "Outro": exige texto. */
  exigeTexto: number;
  ativo: number;
}

export type Mostrar = 'todos' | SituacaoEscolhida;

/** GET /api/v1/manutencao/plantao */
export interface RespostaDoPlantao {
  mes: string;
  mesDeHoje: string;
  filtros: { mes: string; empresa: Empresa | null; clienteId: string | null; tecnico: string | null; mostrar: Mostrar };
  cartoes: CartoesDoPlantao;
  conferencia: LinhaDoPlantao[];
  linhas: LinhaDoPlantao[];
  porTecnico: HorasDoTecnico[];
  naoCobrados: NaoCobrados;
  porMes: { desde: string | null; meses: ValorDoMes[] };
  clientes: Array<{ id: string; nome: string }>;
  tecnicos: string[];
  motivos: MotivoDeNaoCobrar[];
  tabelaRoute: RegraDePreco | null;
  /** 29/10: a tabela de plantão de cliente sem contrato (Configurações). `null` = não cadastrada: o "Lançar" não aparece. */
  tabelaSemContrato?: RegraDePreco | null;
  podeEnviarResumo: boolean;
  /** 01/11/2026: só o admin troca a empresa e exclui (o servidor diz, pela mesma conta da trava). Ausente = não. */
  podeCorrigir?: boolean;
  /** 01/11/2026: os plantões do mês excluídos pelo admin (com os filtros de cliente e técnico). Ausente = nenhum. */
  excluidos?: PlantaoExcluido[];
  pendencias: { aFaturar: number; emConferencia: number };
  /**
   * P10 — as corretivas que passaram da franquia do mês (a mesma conta da aba Contratos), com a MESMA situação por linha.
   * Ausente em resposta antiga = nenhuma.
   */
  excedentes?: {
    linhas: LinhaDoPlantao[];
    conferencia: LinhaDoPlantao[];
    totalCentavos: number;
    faturadoCentavos: number;
    faltaFaturarCentavos: number;
  };
  /**
   * #515 — as corretivas avulsas do mês (sistema sem contrato ativo que o cubra), com a MESMA situação por linha e
   * total próprio. Ausente em resposta antiga = nenhuma.
   */
  avulsas?: {
    linhas: LinhaDoPlantao[];
    conferencia: LinhaDoPlantao[];
    totalCentavos: number;
    faturadoCentavos: number;
    faltaFaturarCentavos: number;
  };
}

/** POST /api/v1/manutencao/plantao/:taskId/situacao */
export interface PedidoDeSituacao {
  situacao: SituacaoEscolhida;
  motivoId?: string | null;
  motivoTexto?: string | null;
  valorCentavos?: number | null;
}

/** POST /api/v1/manutencao/plantao/:taskId/ajuste */
export interface PedidoDeAjuste {
  /** `lancar` (29/10): aplica a tabela de plantão sem contrato, num clique, sem motivo digitado. */
  tipo: 'tempo' | 'valor' | 'regra' | 'dispensar' | 'lancar';
  minutos?: number;
  valorCentavos?: number;
  regra?: RegraDePreco;
  motivo: string;
  motivoId?: string;
}

export type TipoDePdf = 'cliente' | 'igm' | 'route' | 'tecnico' | 'geral';

/** GET /api/v1/manutencao/plantao/config */
export interface ConfigDoPlantao {
  tabelaRoute: RegraDePreco | null;
  limite: number;
  motivos: MotivoDeNaoCobrar[];
  /** #515: o valor por hora da corretiva avulsa, em centavos. `null` = não cadastrado (as avulsas ficam na conferência). */
  avulsaPorHoraCentavos: number | null;
  /** 29/10: a tabela de plantão de cliente sem contrato. `null` = não cadastrada. */
  tabelaSemContrato?: RegraDePreco | null;
}
